import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { RxUtils, Tr } from '@utils';
import { CreateUser, Organization, Role, roleService } from '@services';
import { Divider, Grid, MenuItem } from '@mui/material';
import { VoltaageModalDialog, IVoltaageModalDialogProps, FormikTextfield, FormikSubmitButton } from '@components/common';

type FormFields = Extendable<{
	name: string;
	surname: string;
	email: string;
	password: string;
	roleId: number;
}>;

export interface IOrganizationAssignRoleDialogProps extends IVoltaageModalDialogProps {
	entity?: Organization;
	onPrimaryButtonClick?: (entity: CreateUser) => void;
}

export const OrganizationAssignRoleDialog: FC<IOrganizationAssignRoleDialogProps> = ({ entity, onPrimaryButtonClick, ...props }) => {
	const { t } = useTranslation('portal');
	const [roles, setRoles] = useState<Role[]>([]);
	const formik = useFormik<FormFields>({
		initialValues: { name: '', surname: '', email: '', password: '', roleId: '' },
		validateOnChange: true,
		validationSchema: yup.object({
			name: yup.string().required(t('field-required')),
			surname: yup.string().required(t('field-required')),
			email: yup.string().required(t('field-required')),
			password: yup.string().required(t('field-required')),
			roleId: yup.number().required(t('field-required'))
		}),
		onSubmit: (fields, api) => {
			api.setSubmitting(false);
			onPrimaryButtonClick?.(new CreateUser({ ...fields, organization: entity?.id || '' }));
		}
	});

	useEffect(() => {
		if (entity) {
			RxUtils.promisify(roleService.getAll(entity?.id), setRoles);
		} else {
			setRoles([]);
		}
		formik.resetForm();
	}, [entity]);

	return (
		<VoltaageModalDialog
			title={<Tr.Portal path="organizations.assign-role-dialog-title" options={{ organization: entity?.name || '' }} />}
			dividers
			{...props}
		>
			<form onSubmit={formik.handleSubmit}>
				<Grid container rowSpacing={4} columnSpacing={3} sx={{ paddingX: 14, paddingY: 8, paddingBottom: 4 }}>
					<Grid item xs={6}>
						<FormikTextfield
							formikApi={formik}
							name="name"
							title={t('organizations.first-name')}
							placeholder={t('organizations.first-name-placeholder')}
							fullWidth
							required
						/>
					</Grid>
					<Grid item xs={6}>
						<FormikTextfield
							formikApi={formik}
							name="surname"
							title={t('organizations.last-name')}
							placeholder={t('organizations.last-name-placeholder')}
							fullWidth
							required
						/>
					</Grid>
					<Grid item xs={6}>
						<FormikTextfield
							formikApi={formik}
							name="email"
							title={t('organizations.email')}
							placeholder={t('organizations.email-placeholder')}
							fullWidth
							required
						/>
					</Grid>
					<Grid item xs={6}>
						<FormikTextfield
							formikApi={formik}
							name="password"
							title={t('organizations.password')}
							placeholder={t('organizations.password-placeholder')}
							fullWidth
							required
						/>
					</Grid>
					<Grid item xs={6}>
						<FormikTextfield
							select
							formikApi={formik}
							name="roleId"
							title={t('organizations.role')}
							placeholder={t('organizations.role-placeholder')}
							required
						>
							{(roles || []).map(({ id, name }) => (
								<MenuItem key={`role-${name}-${id}`} value={id}>
									{name}
								</MenuItem>
							))}
						</FormikTextfield>
					</Grid>
					<Grid item xs={12}>
						<Divider light />
					</Grid>
					<Grid item xs={12} container>
						<FormikSubmitButton formik={formik} data-testid="create-organization-user-form-submit-button">
							<Tr.Portal path="organizations.create-user" />
						</FormikSubmitButton>
					</Grid>
				</Grid>
			</form>
		</VoltaageModalDialog>
	);
};
